import { useMemo } from "react";
import { ROLE_SCOPES, ROLES, type Role, type Scope } from "@backstory/protocol";
import { currentOrg, useAuth, type AuthState } from "./store";

/**
 * What each role means, in the words the product shows. The server is the
 * authority (authz.Scopes); this file exists so a control the caller cannot use
 * is hidden or disabled with a reason, not discovered through a 403.
 */
export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  developer: "Developer",
  support: "Support",
  readonly: "Read-only",
};

/** One line per role, for the option under a select. */
export const ROLE_SUMMARY: Record<Role, string> = {
  owner: "Everything, including single sign-on and the last say on billing.",
  admin: "Runs the organization: members, projects, billing, privacy, audit log, AI settings.",
  developer: "Builds against it: projects, SDK keys, notification rules.",
  support: "Works sessions: Live Assist, review, share, triage issues, run AI.",
  readonly: "Sees everything, changes nothing, spends no AI credits.",
};

export interface RoleDetail {
  can: string[];
  cannot: string[];
}

/** The full comparison, for the "What can each role do?" dialog. */
export const ROLE_DETAILS: Record<Role, RoleDetail> = {
  owner: {
    can: [
      "Everything an admin can",
      "Add, change and remove single sign-on connections",
      "Instance settings on a self-hosted deployment",
      "Transfer or hold the last owner seat (the last owner cannot be removed or demoted)",
    ],
    cannot: [],
  },
  admin: {
    can: [
      "Everything a developer and a support member can",
      "Invite, remove and change the role of members",
      "Organization settings, single sign-on, SCIM provisioning",
      "Plan, payment and AI credit changes",
      "Retention and recording settings",
      "Read the audit log",
      "AI settings, providers and routing",
      "Warehouse exports and Teams installations",
    ],
    cannot: ["Configure single sign-on", "Remove or demote the last owner"],
  },
  developer: {
    can: [
      "Everything a support member can, except Live Assist",
      "Create and edit projects, view SDK keys",
      "Notification rules and channels",
    ],
    cannot: ["Live Assist", "Members, billing, audit log, privacy requests", "Organization or AI settings"],
  },
  support: {
    can: [
      "Everything a read-only member can",
      "Live Assist: join a user's session, co-browse and talk",
      "Mark sessions reviewed, create share links and exports",
      "Change issue status and assignee, create funnels and saved segments",
      "Run AI: summaries, explanations, Ask Why, search, draft PRs",
    ],
    cannot: ["Projects or SDK keys", "Members, billing, audit log, privacy requests", "Organization or AI settings"],
  },
  readonly: {
    can: ["Open every page: sessions, replays, issues, heatmaps, funnels, calls, dashboards", "Read AI output that already exists", "Their own profile, inbox and notifications"],
    cannot: ["Change anything", "Run AI or rate its answers", "Live Assist", "Share, export or mark sessions reviewed"],
  },
};

/** Scopes an API token can carry, in the words the token dialog shows. */
export const SCOPE_LABELS: Record<Scope, string> = {
  read: "Read sessions, replays, events, issues and AI output",
  write: "Review and share sessions, triage issues, funnels, segments, source maps",
  ai: "Run AI analyses, chat and search",
  live: "Live Assist",
  tokens: "Manage API tokens",
  members: "Members, invitations and SCIM",
  billing: "Plan and payment changes",
  audit: "Read the audit log",
  admin: "Organization, project and AI settings, privacy requests",
};

export function roleOf(s: AuthState): Role | undefined {
  return currentOrg(s)?.role;
}

export function scopesOf(role: Role | undefined): readonly Scope[] {
  return role ? ROLE_SCOPES[role] : [];
}

export function hasScope(s: AuthState, ...scopes: Scope[]): boolean {
  const own = scopesOf(roleOf(s));
  return scopes.some((sc) => own.includes(sc));
}

export function roleRank(role: Role): number {
  return ROLES.length - ROLES.indexOf(role);
}

/**
 * The caller's capabilities as booleans, so a page reads `can.write` instead
 * of listing role names. Mirrors authz.Scopes on the server.
 */
export function useCan() {
  const auth = useAuth();
  const role = roleOf(auth);
  return useMemo(() => {
    const own = scopesOf(role);
    const has = (...scopes: Scope[]) => scopes.some((sc) => own.includes(sc));
    return {
      role,
      has,
      /** Session review, share links, exports, issue triage, funnels, segments. */
      write: has("write", "admin"),
      /** Run AI and rate its output. */
      ai: has("ai", "admin"),
      live: has("live", "admin"),
      tokens: has("tokens", "admin"),
      members: has("members", "admin"),
      billing: has("billing", "admin"),
      audit: has("audit", "admin"),
      /** Organization, project, privacy, AI and notification administration. */
      admin: has("admin"),
      owner: role === "owner",
      /** Notification rules and channels: developers and above. */
      develop: has("tokens", "admin"),
    };
  }, [role]);
}

export type Can = ReturnType<typeof useCan>;
